import React, { useMemo } from 'react';
import { ShoppingCart, Filter, Package, FileText, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const DailyOrderTab = ({
  selectedDate,
  setSelectedDate,
  selectedSupplier,
  setSelectedSupplier,
  providerOrder,
  allItems,
  loading,
  loadProviderOrder
}) => {
  // Suppliers for daily orders
  const dailySuppliers = useMemo(() => {
    const items = providerOrder?.items || [];
    return [...new Set(items.map(item => item.supplier || 'Sin asignar'))].sort();
  }, [providerOrder]);

  // Filtered items for daily
  const filteredDailyItems = useMemo(() => {
    if (!providerOrder?.items) return [];
    if (selectedSupplier === 'all') return providerOrder.items;
    return providerOrder.items.filter(item => (item.supplier || 'Sin asignar') === selectedSupplier);
  }, [providerOrder, selectedSupplier]);

  // Items con pedidos (ahora final_required = total_requested)
  const itemsNeedingOrder = useMemo(() => {
    return filteredDailyItems.filter(item => item.final_required > 0);
  }, [filteredDailyItems]);

  const filteredTotalQuantity = filteredDailyItems.reduce((sum, item) => sum + item.final_required, 0);

  // Export to TXT
  const exportDailyOrderTXT = () => {
    if (!providerOrder || filteredDailyItems.length === 0) {
      toast.error('No hay pedidos para exportar');
      return;
    }

    const supplierName = selectedSupplier === 'all' ? 'Todos' : selectedSupplier;
    const dateFormatted = selectedDate;
    
    const itemsBySupplier = {};
    filteredDailyItems.forEach(item => {
      const supplier = item.supplier || 'Sin asignar';
      if (!itemsBySupplier[supplier]) {
        itemsBySupplier[supplier] = [];
      }
      itemsBySupplier[supplier].push(item);
    });

    let content = '';
    content += `PEDIDO A PROVEEDORES\n`;
    content += `Fecha: ${dateFormatted}\n`;
    if (selectedSupplier !== 'all') {
      content += `Proveedor: ${supplierName}\n`;
    }
    content += `\n`;

    Object.entries(itemsBySupplier).forEach(([supplier, items]) => {
      content += `--- ${supplier.toUpperCase()} ---\n\n`;
      
      items.forEach(item => {
        if (item.final_required > 0) {
          const itemInfo = allItems.find(i => i.id === item.item_id);
          const unidadesPorCaja = itemInfo?.unidades_por_caja;
          const unidadCompra = itemInfo?.unidad_compra || 'caja';
          
          content += `${item.item_name}\n`;
          content += `  Codigo: ${item.item_code}\n`;
          content += `  Pedido locales: ${item.total_requested}\n`;
          content += `  Stock bodega: ${item.warehouse_stock}\n`;
          content += `  A pedir: ${item.final_required} ${item.unit}\n`;
          
          if (unidadesPorCaja && unidadesPorCaja > 0) {
            const cajasExactas = (item.final_required / unidadesPorCaja).toFixed(2);
            const cajasSugeridas = Math.ceil(item.final_required / unidadesPorCaja);
            content += `  → En ${unidadCompra}s: ${cajasSugeridas} (${cajasExactas} exacto)\n`;
          }
          content += `\n`;
        }
      });
    });

    const totalAPedir = filteredDailyItems.reduce((sum, item) => sum + item.final_required, 0);
    const productosFaltantes = filteredDailyItems.filter(item => item.final_required > 0).length;
    
    content += `---\n`;
    content += `RESUMEN\n`;
    content += `Productos faltantes: ${productosFaltantes}\n`;
    content += `Total a pedir: ${totalAPedir} unidades\n`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileName = selectedSupplier === 'all' 
      ? `pedido_${dateFormatted}.txt`
      : `pedido_${selectedSupplier.replace(/\s+/g, '_')}_${dateFormatted}.txt`;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Pedido exportado: ${fileName}`);
  };

  // Copy to clipboard for WhatsApp
  const copyToClipboardWhatsApp = () => {
    if (!providerOrder || itemsNeedingOrder.length === 0) {
      toast.error('No hay productos faltantes para copiar');
      return;
    }

    const supplierName = selectedSupplier === 'all' ? 'Todos los proveedores' : selectedSupplier;
    const dateFormatted = selectedDate;
    
    const itemsBySupplier = {};
    itemsNeedingOrder.forEach(item => {
      const supplier = item.supplier || 'Sin asignar';
      if (!itemsBySupplier[supplier]) {
        itemsBySupplier[supplier] = [];
      }
      itemsBySupplier[supplier].push(item);
    });

    let content = `*PEDIDO ${dateFormatted}*\n`;
    if (selectedSupplier !== 'all') {
      content += `Proveedor: ${supplierName}\n`;
    }
    content += `\n`;

    Object.entries(itemsBySupplier).forEach(([supplier, items]) => {
      if (selectedSupplier === 'all') {
        content += `*${supplier}*\n`;
      }
      
      items.forEach(item => {
        const itemInfo = allItems.find(i => i.id === item.item_id);
        const unidadesPorCaja = itemInfo?.unidades_por_caja;
        const unidadCompra = itemInfo?.unidad_compra || 'caja';
        
        if (unidadesPorCaja && unidadesPorCaja > 0) {
          const cajasSugeridas = Math.ceil(item.final_required / unidadesPorCaja);
          content += `• ${item.item_name}: *${cajasSugeridas} ${unidadCompra}${cajasSugeridas !== 1 ? 's' : ''}*\n`;
        } else {
          content += `• ${item.item_name}: *${item.final_required} ${item.unit}*\n`;
        }
      });
      content += `\n`;
    });

    navigator.clipboard.writeText(content).then(() => {
      toast.success('Pedido copiado al portapapeles (formato WhatsApp)');
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  return (
    <div className="space-y-6" data-testid="daily-order-tab">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Fecha del Pedido</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid="daily-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Filtrar por Proveedor</label>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger data-testid="daily-supplier-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Todos los proveedores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {dailySuppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadProviderOrder} disabled={loading} data-testid="generate-order-btn">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Generar Pedido
            </Button>
            <Button 
              variant="outline" 
              onClick={copyToClipboardWhatsApp} 
              disabled={!providerOrder || itemsNeedingOrder.length === 0}
              title="Copiar pedido en formato WhatsApp (solo faltantes, con cajas)"
              data-testid="copy-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Copiar WhatsApp
            </Button>
            <Button 
              variant="outline" 
              onClick={exportDailyOrderTXT} 
              disabled={!providerOrder || filteredDailyItems.length === 0}
              data-testid="export-txt-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Exportar TXT
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : providerOrder && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total Productos</p>
                  <p className="text-4xl font-bold">{filteredDailyItems.length}</p>
                </div>
                <Package className="w-12 h-12 opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total Pedido Final</p>
                  <p className="text-4xl font-bold">{filteredTotalQuantity.toFixed(0)}</p>
                </div>
                <ShoppingCart className="w-12 h-12 opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-500 to-slate-600 text-white rounded-lg p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Stock Registrado (Info)</p>
                  <p className="text-4xl font-bold">{filteredDailyItems.reduce((sum, i) => sum + i.warehouse_stock, 0).toFixed(0)}</p>
                </div>
                <Package className="w-12 h-12 opacity-80" />
              </div>
            </div>
          </div>

          {filteredDailyItems.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
              <ShoppingCart className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Sin pedidos</h3>
              <p className="text-muted-foreground">
                No hay pedidos de locales para esta fecha
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b">
                <p className="text-sm text-slate-600">
                  El stock registrado en bodega es informativo y no reduce automáticamente el pedido final.
                </p>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Código</th>
                    <th className="px-4 py-3 text-left font-semibold">Producto</th>
                    <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                    <th className="px-4 py-3 text-right font-semibold">Solicitado por locales</th>
                    <th className="px-4 py-3 text-right font-semibold bg-slate-100">Stock registrado bodega</th>
                    <th className="px-4 py-3 text-right font-semibold bg-amber-50" title="Referencia calculada a partir del stock registrado en sistema">Sugerido considerando stock</th>
                    <th className="px-4 py-3 text-right font-semibold bg-green-50">Pedido final</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDailyItems.map((item) => {
                    const suggestedWithStock = Math.max(0, item.total_requested - item.warehouse_stock);
                    return (
                      <tr key={item.item_id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-4 font-mono text-sm">{item.item_code}</td>
                        <td className="px-4 py-4 font-medium">{item.item_name}</td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md">
                            {item.supplier || 'Sin asignar'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right font-semibold text-slate-900">{item.total_requested} {item.unit}</td>
                        <td className="px-4 py-4 text-right bg-slate-50 text-slate-600">{item.warehouse_stock} {item.unit}</td>
                        <td className="px-4 py-4 text-right bg-amber-50/50 text-amber-700">
                          {suggestedWithStock} {item.unit}
                        </td>
                        <td className="px-4 py-4 text-right bg-green-50/50 font-bold text-green-700">
                          {item.final_required} {item.unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100 font-bold">
                  <tr>
                    <td colSpan="6" className="px-4 py-4 text-right text-slate-900">
                      TOTAL PEDIDO FINAL:
                    </td>
                    <td className="px-4 py-4 text-right text-lg text-green-700">
                      {filteredTotalQuantity.toFixed(1)} unidades
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DailyOrderTab;
